import { Text } from '@mantine/core';
import { DataTable, type DataTableColumn, type DataTableSortStatus } from 'mantine-datatable';
import { StatusBadge } from '../../../components';
import { PAGE_SIZE } from '../../../magic-values';
import { formatDate } from './format-date';
import { JobProgressCell } from './job-progress-cell';
import type { Job } from '../../../types/job';

interface JobsTableProps {
  jobs: Job[];
  fetching?: boolean;
  onRowClick?: (job: Job) => void;
  page: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  sortStatus: DataTableSortStatus;
  onSortStatusChange: (status: DataTableSortStatus) => void;
  height?: string | number;
}

const COLUMNS: DataTableColumn<Job>[] = [
  {
    accessor: 'type',
    title: 'Type',
    render: (job) => <Text size="sm">{job.type}</Text>,
  },
  {
    accessor: 'origin',
    title: 'Origin',
    render: (job) =>
      job.origin ? (
        <Text size="xs" sx={{ fontFamily: 'monospace' }}>
          {job.origin}
        </Text>
      ) : (
        '—'
      ),
  },
  {
    accessor: 'status',
    title: 'Status',
    render: (job) => <StatusBadge status={job.status} />,
  },
  {
    accessor: 'progress',
    title: 'Progress',
    render: (job) => <JobProgressCell progress={job.progress} />,
  },
  {
    accessor: 'trigger',
    title: 'Trigger',
    render: (job) => job.trigger ?? '—',
  },
  {
    accessor: 'initiatedBy',
    title: 'Initiated by',
    render: (job) => job.initiatedBy?.label ?? job.initiatedBy?.kind ?? '—',
  },
  {
    accessor: 'createdAt',
    title: 'Created',
    sortable: true,
    render: (job) => formatDate(job.createdAt),
  },
  {
    accessor: 'finishedAt',
    title: 'Finished',
    sortable: true,
    render: (job) => formatDate(job.finishedAt),
  },
];

export function JobsTable({
  jobs,
  fetching,
  onRowClick,
  page,
  totalRecords,
  onPageChange,
  sortStatus,
  onSortStatusChange,
  height = 'calc(100vh - 500px)',
}: JobsTableProps) {
  return (
    <DataTable
      striped
      highlightOnHover
      height={height}
      sx={{ minWidth: 1000 }}
      records={jobs}
      idAccessor="_id"
      fetching={fetching}
      noRecordsText="No jobs."
      columns={COLUMNS}
      onRowClick={onRowClick ? (job) => onRowClick(job) : undefined}
      totalRecords={totalRecords}
      recordsPerPage={PAGE_SIZE}
      page={page}
      onPageChange={onPageChange}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
    />
  );
}
